// Released evidence (Spec docs/spec-evidence-board-v2.md §4b, P3b) — the ONE consumer feed
// (GET /api/evi-claims → app/services/evi_feed.py). Shared by the Claims tab, /claims, the claim
// deck and /claims/:id. Only candidates whose release status is "released" ever arrive here;
// drop/safety never do. Cards are the Variant-A SVGs from our own renderer, inlined (dark mode).
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import clsx from "clsx";
import { InlineSvg } from "./EvidenceReviewTab";

export interface ReleasedClaim {
  id: string;
  claim_id: string;
  view_class: "use" | "use_framed";
  headline_en: string;
  consumer_headline: string;
  headline_key: string | null;
  effect_area_en: string | null;
  benefit_area: string | null;
  design_line_en: string;
  source_en: string;
  significance_gate: string | null;
  effect_value: string | null;
  strength_tier: string | null;
  source_not_nameable: boolean;
  caveat: string | null;
  has_caveat: boolean;
  chart_type: string | null;
  evidence_url: string;
  card_svg: string;
  card_png: string;
  chart_svg: string | null;
  chart_png: string | null;
}

export const CLASS_LABEL: Record<ReleasedClaim["view_class"], string> = {
  use: "Use",
  use_framed: "Use (framed)",
};

/** Group by English effect area, keeping the feed order (strongest first) inside each group. */
export function groupByArea(claims: ReleasedClaim[]): { area: string; items: ReleasedClaim[] }[] {
  const out: { area: string; items: ReleasedClaim[] }[] = [];
  const idx = new Map<string, number>();
  for (const c of claims) {
    const area = c.effect_area_en || "Other";
    let i = idx.get(area);
    if (i === undefined) {
      i = out.length;
      idx.set(area, i);
      out.push({ area, items: [] });
    }
    out[i].items.push(c);
  }
  return out;
}

/** One released card: the card SVG + a slim action row (details + proof on the Evidence board). */
export function ReleasedClaimTile({ claim, className }: { claim: ReleasedClaim; className?: string }) {
  return (
    <article
      className={clsx("flex min-w-0 flex-col gap-2 rounded-card border border-border bg-surface p-3", className)}
      data-released-card={claim.claim_id}
      data-released-class={claim.view_class}
    >
      <Link to={`/claims/${encodeURIComponent(claim.claim_id)}`} className="block rounded-[12px] transition hover:opacity-90">
        <InlineSvg url={claim.card_svg} label={claim.headline_en} />
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className={clsx(
              "rounded-chip px-2 py-0.5 text-[0.72rem] font-bold text-white",
              claim.view_class === "use" ? "bg-success" : "bg-info",
            )}
          >
            {CLASS_LABEL[claim.view_class]}
          </span>
          {claim.effect_area_en && <span className="text-text-3">{claim.effect_area_en}</span>}
          {claim.source_not_nameable && (
            <span className="rounded-chip border border-warning/60 px-2 py-0.5 text-[0.7rem] font-medium text-warning">
              Source not nameable
            </span>
          )}
        </div>
        <Link
          to={claim.evidence_url}
          className="inline-flex items-center gap-1 font-medium text-accent-700 hover:text-accent-600 dark:text-accent-300"
          data-released-proof
        >
          Proof <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      {claim.has_caveat && claim.caveat && <p className="text-xs leading-snug text-text-3">{claim.caveat}</p>}
    </article>
  );
}
